import { useMemo, useState, useEffect } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_ExpandAllButton,
  MRT_ToggleGlobalFilterButton,
  MRT_ToggleFiltersButton,
} from "material-react-table";
import CustomGlobalFilter from "./CustomGlobalFilter";
import {
  Box,
  Tooltip,
  Button,
  Stack,
  Typography,
  MenuItem,
  Menu,
  Divider,
  Popper,
  Popover,
} from "@mui/material";
import {
  Save,
  FilterListOutlined,
  FileDownloadOutlined,
  TableRowsOutlined,
} from "@mui/icons-material";
import { mkConfig, generateCsv, download } from "export-to-csv";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf"; //or use your library of choice here
import autoTable from "jspdf-autotable";
import {
  MRT_GlobalFilterTextField,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFullScreenButton,
} from "material-react-table";
import { Close, FileDownload } from "@mui/icons-material"; // Add this import at the top
import { IconButton } from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

// Add to props list
const MUIDataTable = ({
  tableTheme,
  columns,
  data,
  rowCount,
  title,
  enableStickyHeader = false,
  enableRowSelection,
  enableColumnFilters = true,
  enableGlobalFilter = true,
  enablePagination = true,
  enableSorting = true,
  enableGrouping = true,
  paginationDisplayMode = "pages",
  enableFullScreenToggle = true,
  enableHiding = true,
  onRowSelectionChange,
  selectedRows,
  renderTopToolbarCustomActions,
  onAddNewRow,
  rowHoverColor,
  initialState = {},
  muiTableBodyRowProps,
  muiTableBodyCellProps,
  positionToolbarAlertBanner,
  getRowId,
  icons,
  savedViews = [],
  // Add these props
  onGlobalFilterChange,
  onColumnFiltersChange,
  onPaginationChange,
  onSortingChange,

  createDisplayMode = "modal",
  editDisplayMode = "modal",
  enableEditing = false,
  onCreatingRowCancel,
  onCreatingRowSave,
  onEditingRowCancel,
  onEditingRowSave,
  renderCreateRowDialogContent,
  renderEditRowDialogContent,
  renderRowActions,
  muiPaginationProps,
  renderToolbarInternalActions,
  renderBottomToolbarCustomActions,
  renderBottomToolbar,
  renderTopToolbar,
  manualGrouping,
  enableVerticalBorders = false,
  //Virtual infinite scroll
  maxHeight,
  enableRowVirtualization = false,
  enableInfiniteScroll = false,
  onInfiniteScroll,
  isLoading,
  enableColumnResizing,
  enableExport = false,
  exportType = "csv",
  exportRowData,
  headerFontColor, // Add this prop with default color
  headerBackgroundColor, // Add this prop with default color
  onSaveTableState, // Add this prop
  onDeleteTableState,
}) => {
  // Modify the saveTableState function
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [viewName, setViewName] = useState("");

  const saveTableState = () => {
    setSaveViewOpen(true);
  };

  const handleViewSelect = (view) => {
    if (view.columnFilters) table.setColumnFilters(view.columnFilters);
    if (view.globalFilter) table.setGlobalFilter(view.globalFilter);
    if (view.sorting) table.setSorting(view.sorting);
    if (view.pagination) table.setPagination(view.pagination);
    if (view.columnVisibility) table.setColumnVisibility(view.columnVisibility);
    handleMenuClose();
  };

  useEffect(() => {
    // Load saved table state from localStorage if available
    const savedState = localStorage.getItem("tableState");
    if (savedState) {
      const parsedState = JSON.parse(savedState);
      onColumnFiltersChange(parsedState.columnFilters || []);
      onGlobalFilterChange(parsedState.globalFilter || "");
      onSortingChange(parsedState.sorting || []);
      onPaginationChange(
        parsedState.pagination || { pageIndex: 0, pageSize: 10 }
      );
      onColumnFiltersChange(parsedState.columnVisibility || {});
    }
  }, []);

  const handleExportRows = (
    rows,
    exportType,
    fileName = crypto.randomUUID()
  ) => {
    const rowData = rows.map((row) => row.original);

    if (exportType === "excel") {
      const ws = XLSX.utils.json_to_sheet(rowData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Data");
      XLSX.writeFile(wb, `${fileName}.xlsx`);
    } else if (exportType === "pdf") {
      const doc = new jsPDF();
      const visibleColumns = columns.filter((col) => !col.hidden);
      const tableHeaders = visibleColumns.map((col) => col.header);

      const tableData = rows.map((row) =>
        visibleColumns.map((col) => {
          const value = row.original[col.accessorKey];
          return value !== undefined ? value.toString() : "";
        })
      );

      autoTable(doc, {
        head: [tableHeaders],
        body: tableData,
        styles: {
          overflow: "linebreak",
          cellWidth: "wrap",
          halign: "left",
        },
        horizontalPageBreak: true,
        didParseCell: function (data) {
          if (data.section === "head") {
            data.cell.styles.cellWidth = "auto";
          }
        },
      });

      doc.save(`${fileName}.pdf`);
    } else {
      const csvConfig = mkConfig({
        fieldSeparator: ",",
        decimalSeparator: ".",
        useKeysAsHeaders: true,
        filename: fileName,
      });

      const csv = generateCsv(csvConfig)(rowData);
      download(csvConfig)(csv);
    }
  };

  const handleInfiniteScroll = () => {
    if (onInfiniteScroll && !isLoading) {
      onInfiniteScroll();
    }
  };

  const [exportAnchorEl, setExportAnchorEl] = useState(null);

  const handleExportMenuOpen = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportAnchorEl(null);
  };
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  //   const handleMenuSelect = (action) => {
  //     if (onActionSelect) {
  //       onActionSelect(action);
  //     }
  //     handleMenuClose();
  //   };

  const table = useMaterialReactTable({
    muiColumnMenuProps: {
      sx: {
        "& .MuiMenuItem-root": {
          whiteSpace: "normal", // Allow wrapping
          maxWidth: 200, // Optional: limit width to prevent overflow
        },
      },
    },
    // muiTableHeadCellFilterTextFieldProps: {
    //     sx: {
    //         '& .MuiButtonBase-root.MuiIconButton-root': {
    //             margin: '-8px -4px !important',
    //         },
    //     },
    // },
    // muiTableHeadCellProps: {
    //     sx: {
    //         '& .MuiButtonBase-root': {
    //             margin: '-8px -4px !important',     // backup override here too
    //         },
    //         '& .MuiIconButton-root': {
    //             margin: '-8px -4px !important',     // backup override here too
    //         },
    //     },
    // },
    manualFiltering: true,
    manualSorting: true,
    manualPagination: true,
    enableGlobalFilterMatchHighlighting: false,
    enableColumnFilterMatchHighlighting: false,
    enableFilterMatchHighlighting: false,
    columns,
    data,
    enableRowSelection,
    enableColumnFilters,
    enableGlobalFilter,
    enablePagination,
    enableSorting,
    paginationDisplayMode,
    createDisplayMode,
    editDisplayMode,
    enableEditing,
    onCreatingRowCancel,
    onCreatingRowSave,
    onEditingRowCancel,
    onEditingRowSave,
    renderCreateRowDialogContent,
    renderEditRowDialogContent,
    renderRowActions,
    // enableColumnVisibility,
    enableDensityToggle: false,
    enableFullScreenToggle,
    enableRowVirtualization,
    enableHiding,
    enableGrouping,
    getRowId,
    manualGrouping,
    groupedColumnMode: "remove",

    initialState: {
      showGlobalFilter: tableTheme === "CMMS" ? true : false,
    },
    enableColumnResizing,
    displayColumnDefOptions: {
      "mrt-row-actions": {
        header: "",
        size: 50,
      },
      "mrt-row-select": {
        size: 50,

        muiTableBodyCellProps: ({ row }) => ({
          sx: {
            borderLeft: row.getIsSelected() ? "3px solid #007bff" : "none",
            transition: "all 0.1s ease-in",
            borderRight: enableVerticalBorders ? "1px solid #E5E7EB" : "none",
            "&:last-child": {
              borderRight: "none",
            },
          },
        }),
      },
      "mrt-row-expand": {
        Header: () => (
          <Stack direction="row" alignItems="center">
            <MRT_ExpandAllButton table={table} />
            <Box>Groups</Box>
          </Stack>
        ),
        GroupedCell: ({ row, table }) => {
          const { grouping } = table.getState();
          return row.getValue(grouping[grouping.length - 1]);
        },
        enableResizing: true,

        size: 200,
      },
    },
    onColumnFiltersChange: onColumnFiltersChange || null,
    onGlobalFilterChange: onGlobalFilterChange || null,
    onPaginationChange: onPaginationChange || null,
    onSortingChange: onSortingChange || null,
    enableStickyHeader: enableInfiniteScroll ? true : enableStickyHeader,
    onRowSelectionChange,
    rowVirtualizerOptions: { overscan: 20 },
    renderToolbarInternalActions: renderToolbarInternalActions || null,
    renderBottomToolbarCustomActions,
    renderBottomToolbar,

    // Update title typography
    renderTopToolbar:
      renderTopToolbar ||
      (({ table }) => {
        switch (tableTheme) {
          case "CMMS":
            return (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                  p: { xs: "12px", sm: "16px" },
                }}
              >
                {/* Title and Controls Row */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "stretch",
                    gap: { xs: "0.5rem", sm: "1rem" },
                    justifyContent: "space-between",
                    flexDirection: { xs: "column", sm: "row" },
                    width: "100%",
                  }}
                >
                  {/* Search Box Container */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: { xs: "100%", md: "40%" },
                    }}
                  >
                    <CustomGlobalFilter
                      value={table.getState().globalFilter}
                      onChange={(e) => table.setGlobalFilter(e.target.value)}
                      placeholder="Search all records..."
                    />
                  </Box>

                  {/* Actions Container */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.5rem",
                      alignItems: "center",
                      flexWrap: "wrap",
                      justifyContent: { xs: "flex-start", sm: "flex-end" },
                      width: { xs: "100%", md: "auto" },
                    }}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      sx={{
                        textTransform: "none",
                        backgroundColor: "#2563EB",
                        height: "40px",
                        minWidth: { xs: "100%", sm: "auto" },
                        "&:hover": {
                          backgroundColor: "#1E40AF",
                        },
                      }}
                      onClick={onAddNewRow}
                      startIcon={<AddIcon />}
                    >
                      Add New Row
                    </Button>

                    {enableExport && (
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          textTransform: "none",
                          backgroundColor: "#2563EB",
                          height: "40px",
                          minWidth: { xs: "100%", sm: "auto" },
                          "&:hover": {
                            backgroundColor: "#1E40AF",
                          },
                        }}
                        onClick={() => {
                          handleExportRows(
                            table.getPrePaginationRowModel().rows,
                            "csv"
                          );
                        }}
                      >
                        Export As CSV
                      </Button>
                    )}

                    {renderTopToolbarCustomActions &&
                      renderTopToolbarCustomActions({ table })}

                    <Box
                      sx={{
                        display: "flex",
                        gap: "0.5rem",
                        marginLeft: { xs: 0, sm: "0.5rem" },
                      }}
                    >
                      <MRT_ToggleFiltersButton table={table} />
                      <MRT_ToggleFullScreenButton table={table} />
                    </Box>
                  </Box>
                </Box>

                {/* Rest of the code remains the same */}
                {table.getState().grouping.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.3rem",
                      alignItems: "center",
                      borderRadius: "6px",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "#374151", fontWeight: 500 }}
                    >
                      Grouped by:
                    </Typography>
                    {table.getState().grouping.map((columnId) => {
                      const column = table.getColumn(columnId);
                      return (
                        <Box
                          key={columnId}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            backgroundColor: "#EFF6FF",
                            padding: "4px 12px",
                            borderRadius: "16px",
                            border: "1px solid #BFDBFE",
                          }}
                        >
                          <Typography variant="body2" sx={{ color: "#1E40AF" }}>
                            {column.columnDef.header}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => {
                              column.toggleGrouping();
                            }}
                            sx={{
                              padding: "2px",
                              marginLeft: "4px",
                              "&:hover": {
                                backgroundColor: "#DBEAFE",
                              },
                            }}
                          >
                            <Close
                              sx={{ fontSize: "16px", color: "#1E40AF" }}
                            />
                          </IconButton>
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {/* Selection and Grouping Status */}
                {enableRowSelection &&
                  table.getSelectedRowModel().rows.length > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        backgroundColor: "#EFF6FF",
                        padding: "8px 16px",
                        borderRadius: "6px",
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "#1E40AF" }}>
                        {table.getSelectedRowModel().rows.length} of {rowCount}{" "}
                        row(s) selected
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => table.resetRowSelection()}
                        sx={{
                          color: "#2563EB",
                          textTransform: "none",
                          "&:hover": {
                            backgroundColor: "transparent",
                          },
                        }}
                      >
                        Clear selection
                      </Button>
                    </Box>
                  )}
              </Box>
            );
          case "DMS":
            return (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                  p: { xs: "12px", sm: "16px" },
                }}
              >
                {/* Title and Controls Row */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "stretch",
                    gap: { xs: "0.5rem", sm: "1rem" },
                    justifyContent: "space-between",
                    flexDirection: { xs: "column", sm: "row" },
                    width: "100%",
                  }}
                >
                  {/* Search Box Container */}

                  {/* Actions Container */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.5rem",
                      alignItems: "center",
                      flexWrap: "wrap",
                      justifyContent: { xs: "flex-start", sm: "flex-end" },
                      width: { xs: "100%", md: "auto" },
                    }}
                  >
                    {enableExport && (
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          textTransform: "none",
                          backgroundColor: "#2563EB",
                          height: "40px",
                          minWidth: { xs: "100%", sm: "auto" },
                          "&:hover": {
                            backgroundColor: "#1E40AF",
                          },
                        }}
                        onClick={() => {
                          handleExportRows(
                            table.getPrePaginationRowModel().rows,
                            "csv"
                          );
                        }}
                      >
                        Export As CSV
                      </Button>
                    )}

                    {renderTopToolbarCustomActions &&
                      renderTopToolbarCustomActions({ table })}

                    <Box
                      sx={{
                        display: "flex",
                        gap: "0.5rem",
                        marginLeft: { xs: 0, sm: "0.5rem" },
                      }}
                    >
                      <MRT_ToggleFiltersButton table={table} />
                      <MRT_ToggleFullScreenButton table={table} />
                    </Box>
                  </Box>
                </Box>

                {/* Rest of the code remains the same */}
                {table.getState().grouping.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.3rem",
                      alignItems: "center",
                      borderRadius: "6px",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "#226eb7", fontWeight: 500 }}
                    >
                      Grouped by:
                    </Typography>
                    {table.getState().grouping.map((columnId) => {
                      const column = table.getColumn(columnId);
                      return (
                        <Box
                          key={columnId}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            backgroundColor: "#EFF6FF",
                            padding: "4px 12px",
                            borderRadius: "16px",
                            border: "1px solid #BFDBFE",
                          }}
                        >
                          <Typography variant="body2" sx={{ color: "#1E40AF" }}>
                            {column.columnDef.header}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => {
                              column.toggleGrouping();
                            }}
                            sx={{
                              padding: "2px",
                              marginLeft: "4px",
                              "&:hover": {
                                backgroundColor: "#DBEAFE",
                              },
                            }}
                          >
                            <Close
                              sx={{ fontSize: "16px", color: "#1E40AF" }}
                            />
                          </IconButton>
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {/* Selection and Grouping Status */}
                {enableRowSelection &&
                  table.getSelectedRowModel().rows.length > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        backgroundColor: "#EFF6FF",
                        padding: "8px 16px",
                        borderRadius: "6px",
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "#1E40AF" }}>
                        {table.getSelectedRowModel().rows.length} of {rowCount}{" "}
                        row(s) selected
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => table.resetRowSelection()}
                        sx={{
                          color: "#2563EB",
                          textTransform: "none",
                          "&:hover": {
                            backgroundColor: "transparent",
                          },
                        }}
                      >
                        Clear selection
                      </Button>
                    </Box>
                  )}
              </Box>
            );
          case "RMS":
            return (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                  p: { xs: "12px", sm: "16px" },
                }}
              >
                {/* Title and Controls Row */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "stretch",
                    gap: { xs: "0.5rem", sm: "1rem" },
                    justifyContent: "space-between",
                    flexDirection: { xs: "column", sm: "row" },
                    width: "100%",
                  }}
                >
                  {/* Search Box Container */}

                  {/* Actions Container */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.5rem",
                      alignItems: "center",
                      flexWrap: "wrap",
                      justifyContent: { xs: "flex-end", sm: "flex-end" },
                      width: { xs: "100%", md: "auto" },
                    }}
                  >
                    {enableExport && (
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          textTransform: "none",
                          backgroundColor: "#2563EB",
                          height: "40px",
                          minWidth: { xs: "100%", sm: "auto" },
                          "&:hover": {
                            backgroundColor: "#1E40AF",
                          },
                        }}
                        onClick={() => {
                          handleExportRows(
                            table.getPrePaginationRowModel().rows,
                            "csv"
                          );
                        }}
                      >
                        Export As CSV
                      </Button>
                    )}

                    {renderTopToolbarCustomActions &&
                      renderTopToolbarCustomActions({ table })}

                    <Box
                      sx={{
                        display: "flex",
                        gap: "0.5rem",
                        marginLeft: { xs: 0, sm: "0.5rem" },
                      }}
                    >
                      <MRT_ToggleFiltersButton table={table} />
                      <MRT_ToggleFullScreenButton table={table} />
                    </Box>
                  </Box>
                </Box>

                {/* Rest of the code remains the same */}
                {table.getState().grouping.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.3rem",
                      alignItems: "center",
                      borderRadius: "6px",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "#226eb7", fontWeight: 500 }}
                    >
                      Grouped by:
                    </Typography>
                    {table.getState().grouping.map((columnId) => {
                      const column = table.getColumn(columnId);
                      return (
                        <Box
                          key={columnId}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            backgroundColor: "#EFF6FF",
                            padding: "4px 12px",
                            borderRadius: "16px",
                            border: "1px solid #BFDBFE",
                          }}
                        >
                          <Typography variant="body2" sx={{ color: "#1E40AF" }}>
                            {column.columnDef.header}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => {
                              column.toggleGrouping();
                            }}
                            sx={{
                              padding: "2px",
                              marginLeft: "4px",
                              "&:hover": {
                                backgroundColor: "#DBEAFE",
                              },
                            }}
                          >
                            <Close
                              sx={{ fontSize: "16px", color: "#1E40AF" }}
                            />
                          </IconButton>
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {/* Selection and Grouping Status */}
                {enableRowSelection &&
                  table.getSelectedRowModel().rows.length > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        backgroundColor: "#EFF6FF",
                        padding: "8px 16px",
                        borderRadius: "6px",
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "#1E40AF" }}>
                        {table.getSelectedRowModel().rows.length} of {rowCount}{" "}
                        row(s) selected
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => table.resetRowSelection()}
                        sx={{
                          color: "#2563EB",
                          textTransform: "none",
                          "&:hover": {
                            backgroundColor: "transparent",
                          },
                        }}
                      >
                        Clear selection
                      </Button>
                    </Box>
                  )}
              </Box>
            );
          case "TenantAdmin":
            return (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                  p: { xs: "12px", sm: "16px" },
                }}
              >
                {/* Title and Controls Row */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "stretch",
                    gap: { xs: "0.5rem", sm: "1rem" },
                    justifyContent: "flex-end",
                    flexDirection: { xs: "column", sm: "row" },
                    width: "100%",
                  }}
                >
                  {/* Search Box Container */}

                  {/* Actions Container */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.5rem",
                      alignItems: "center",
                      flexWrap: "wrap",
                      justifyContent: { xs: "flex-end", sm: "flex-end" },
                      width: { xs: "100%", md: "auto" },
                    }}
                  >
                    {enableExport && (
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          textTransform: "none",
                          backgroundColor: "#2563EB",
                          height: "40px",
                          minWidth: { xs: "100%", sm: "auto" },
                          "&:hover": {
                            backgroundColor: "#1E40AF",
                          },
                        }}
                        onClick={() => {
                          handleExportRows(
                            table.getPrePaginationRowModel().rows,
                            "csv"
                          );
                        }}
                      >
                        Export As CSV
                      </Button>
                    )}

                    {renderTopToolbarCustomActions &&
                      renderTopToolbarCustomActions({ table })}
                    <MRT_ShowHideColumnsButton table={table} />
                    <Box
                      sx={{
                        display: "flex",
                        gap: "0.5rem",
                        marginLeft: { xs: 0, sm: "0.5rem" },
                      }}
                    >
                      <MRT_ToggleFiltersButton table={table} />
                      <MRT_ToggleFullScreenButton table={table} />
                    </Box>
                  </Box>
                </Box>

                {/* Rest of the code remains the same */}
                {table.getState().grouping.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.3rem",
                      alignItems: "center",
                      borderRadius: "6px",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "#226eb7", fontWeight: 500 }}
                    >
                      Grouped by:
                    </Typography>
                    {table.getState().grouping.map((columnId) => {
                      const column = table.getColumn(columnId);
                      return (
                        <Box
                          key={columnId}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            backgroundColor: "#EFF6FF",
                            padding: "4px 12px",
                            borderRadius: "16px",
                            border: "1px solid #BFDBFE",
                          }}
                        >
                          <Typography variant="body2" sx={{ color: "#1E40AF" }}>
                            {column.columnDef.header}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => {
                              column.toggleGrouping();
                            }}
                            sx={{
                              padding: "2px",
                              marginLeft: "4px",
                              "&:hover": {
                                backgroundColor: "#DBEAFE",
                              },
                            }}
                          >
                            <Close
                              sx={{ fontSize: "16px", color: "#1E40AF" }}
                            />
                          </IconButton>
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {/* Selection and Grouping Status */}
                {enableRowSelection &&
                  table.getSelectedRowModel().rows.length > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        backgroundColor: "#EFF6FF",
                        padding: "8px 16px",
                        borderRadius: "6px",
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "#1E40AF" }}>
                        {table.getSelectedRowModel().rows.length} of {rowCount}{" "}
                        row(s) selected
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => table.resetRowSelection()}
                        sx={{
                          color: "#2563EB",
                          textTransform: "none",
                          "&:hover": {
                            backgroundColor: "transparent",
                          },
                        }}
                      >
                        Clear selection
                      </Button>
                    </Box>
                  )}
              </Box>
            );
          default:
            return (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                  p: "16px",
                }}
              >
                {/* Title and Controls Row */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "1rem" }}
                  >
                    {title && (
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 400,
                          fontSize: "1.125rem",
                          letterSpacing: "-0.01em",
                          color: "#111827",
                        }}
                      >
                        {title}
                      </Typography>
                    )}
                    <IconButton
                      size="small"
                      onClick={handleMenuOpen}
                      sx={{
                        padding: "4px",
                        color: "#6B7280",
                        "&:hover": {
                          backgroundColor: "#F3F4F6",
                        },
                      }}
                    >
                      <FilterListOutlined />
                    </IconButton>
                    <Popover
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleMenuClose}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                      }}
                      PaperProps={{
                        sx: {
                          width: 250,
                          p: 1,
                        },
                      }}
                    >
                      <Box sx={{ p: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: "#374151",
                            fontWeight: 600,
                            mb: 1,
                          }}
                        >
                          Saved Views
                        </Typography>
                        <Divider sx={{ mb: 1 }} />
                        {savedViews.length > 0 ? (
                          savedViews.map((view, index) => (
                            <MenuItem
                              key={index}
                              onClick={() => handleViewSelect(view)}
                              sx={{
                                borderRadius: "4px",
                                mb: 0.5,
                              }}
                            >
                              {view.name}
                            </MenuItem>
                          ))
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#6B7280",
                              textAlign: "center",
                              py: 2,
                            }}
                          >
                            No saved views
                          </Typography>
                        )}
                      </Box>
                    </Popover>
                  </Box>

                  {/* Right side controls */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.2rem",
                      alignItems: "center",
                    }}
                  >
                    <MRT_GlobalFilterTextField table={table} />
                    <MRT_ToggleGlobalFilterButton table={table} />

                    {/* here custom actions should render */}
                    {renderTopToolbarCustomActions &&
                      renderTopToolbarCustomActions({ table })}

                    {enableExport && (
                      <>
                        {enableExport && (
                          <>
                            {enableRowSelection ? (
                              <>
                                <Tooltip title="Export">
                                  <IconButton
                                    size="small"
                                    onClick={handleExportMenuOpen}
                                    sx={{
                                      padding: "4px",
                                      color: "#6B7280",
                                      "&:hover": {
                                        backgroundColor: "#F3F4F6",
                                      },
                                    }}
                                  >
                                    <FileDownloadOutlined />
                                  </IconButton>
                                </Tooltip>
                                <Menu
                                  anchorEl={exportAnchorEl}
                                  open={Boolean(exportAnchorEl)}
                                  onClose={handleExportMenuClose}
                                  anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "right",
                                  }}
                                  transformOrigin={{
                                    vertical: "top",
                                    horizontal: "right",
                                  }}
                                >
                                  <MenuItem
                                    onClick={() => {
                                      handleExportRows(
                                        table.getPrePaginationRowModel().rows,
                                        exportType
                                      );
                                      handleExportMenuClose();
                                    }}
                                  >
                                    <FileDownloadOutlined
                                      sx={{ mr: 1, fontSize: "1.25rem" }}
                                    />
                                    Export All ({rowCount})
                                  </MenuItem>
                                  {table.getSelectedRowModel().rows.length >
                                    0 && (
                                    <MenuItem
                                      onClick={() => {
                                        handleExportRows(
                                          table.getSelectedRowModel().rows,
                                          exportType
                                        );
                                        handleExportMenuClose();
                                      }}
                                    >
                                      <TableRowsOutlined
                                        sx={{ mr: 1, fontSize: "1.25rem" }}
                                      />
                                      Export Selected (
                                      {table.getSelectedRowModel().rows.length})
                                    </MenuItem>
                                  )}
                                </Menu>
                              </>
                            ) : (
                              <Tooltip title="Export">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleExportRows(
                                      exportRowData ||
                                        table.getPrePaginationRowModel().rows,
                                      exportType
                                    )
                                  }
                                  sx={{
                                    padding: "4px",
                                    color: "#6B7280",
                                    "&:hover": {
                                      backgroundColor: "#F3F4F6",
                                    },
                                  }}
                                >
                                  <FileDownload />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        )}
                      </>
                    )}
                    <MRT_ToggleFiltersButton table={table} />
                    <MRT_ShowHideColumnsButton table={table} />
                    <Tooltip title="Save View">
                      <IconButton
                        size="small"
                        onClick={saveTableState}
                        sx={{
                          padding: "4px",
                          color: "#6B7280",
                          "&:hover": {
                            backgroundColor: "#F3F4F6",
                          },
                        }}
                      >
                        <Save />
                      </IconButton>
                    </Tooltip>
                    <MRT_ToggleFullScreenButton table={table} />
                  </Box>
                </Box>

                {/* Grouping Row */}
                {table.getState().grouping.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.3rem",
                      alignItems: "center",
                      borderRadius: "6px",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "#374151", fontWeight: 500 }}
                    >
                      Grouped by:
                    </Typography>
                    {table.getState().grouping.map((columnId) => {
                      const column = table.getColumn(columnId);
                      return (
                        <Box
                          key={columnId}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            backgroundColor: "#EFF6FF",
                            padding: "4px 12px",
                            borderRadius: "16px",
                            border: "1px solid #BFDBFE",
                          }}
                        >
                          <Typography variant="body2" sx={{ color: "#1E40AF" }}>
                            {column.columnDef.header}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => {
                              column.toggleGrouping();
                            }}
                            sx={{
                              padding: "2px",
                              marginLeft: "4px",
                              "&:hover": {
                                backgroundColor: "#DBEAFE",
                              },
                            }}
                          >
                            <Close
                              sx={{ fontSize: "16px", color: "#1E40AF" }}
                            />
                          </IconButton>
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {/* Selection and Grouping Status */}
                {enableRowSelection &&
                  table.getSelectedRowModel().rows.length > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        backgroundColor: "#EFF6FF",
                        padding: "8px 16px",
                        borderRadius: "6px",
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "#1E40AF" }}>
                        {table.getSelectedRowModel().rows.length} of {rowCount}{" "}
                        row(s) selected
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => table.resetRowSelection()}
                        sx={{
                          color: "#2563EB",
                          textTransform: "none",
                          "&:hover": {
                            backgroundColor: "transparent",
                          },
                        }}
                      >
                        Clear selection
                      </Button>
                    </Box>
                  )}

                {/* Grouping info - similar style
                {table.getState().grouping.length > 0 && (
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: '#EFF6FF',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    mt: table.getSelectedRowModel().rows.length > 0 ? 1 : 0,
                  }}>
                    <Typography variant="body2" sx={{ color: '#1E40AF' }}>
                      Grouped by: {table.getState().grouping.map(columnId => {
                        const column = table.getColumn(columnId);
                        return column.columnDef.header;
                      }).join(', ')}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => table.setGrouping([])}
                      sx={{
                        color: '#2563EB',
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      Clear grouping
                    </Button>
                  </Box>
                )} */}

                {/* Selection Row
                {enableRowSelection && table.getSelectedRowModel().rows.length > 0 && (
                  <Box sx={{
                    display: 'flex',
                    gap: '0.3rem',
                    alignItems: 'center',
                    borderRadius: '6px',
                  }}>
                    <Typography variant="body2" sx={{ color: '#374151', fontWeight: 500 }}>
                      Selected:
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        backgroundColor: '#F0FDF4',
                        padding: '4px 12px',
                        borderRadius: '16px',
                        border: '1px solid #BBF7D0',
                      }}
                    >
                      <Typography variant="body2" sx={{ color: '#166534' }}>
                        {table.getSelectedRowModel().rows.length} of {rowCount} rows
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => table.resetRowSelection()}
                        sx={{
                          padding: '2px',
                          marginLeft: '4px',
                          '&:hover': {
                            backgroundColor: '#DCFCE7',
                          },
                        }}
                      >
                        <Close sx={{ fontSize: '16px', color: '#166534' }} />
                      </IconButton>
                    </Box>
                  </Box>
                )} */}
              </Box>
            );
        }
      }),
    renderTopToolbarCustomActions: renderTopToolbarCustomActions || null,
    state: {
      ...initialState,
      columnOrder: [
        ...(enableRowSelection ? ["mrt-row-select"] : []),
        ...(enableGrouping ? ["mrt-row-expand"] : []),
        ...columns.map((col) => col.accessorKey),
      ],
      showProgressBars: isLoading,
    },
    muiTableContainerProps: enableInfiniteScroll
      ? {
          sx: {
            maxHeight: maxHeight,
            "&::-webkit-scrollbar": {
              height: "8px",
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#F1F1F1",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#BFBFBF",
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: "#A1A1A1",
              },
            },
          },
          onScroll: (event) => {
            const { scrollHeight, scrollTop, clientHeight } = event.target;
            if (scrollHeight - scrollTop - clientHeight < 700) {
              handleInfiniteScroll();
            }
          },
        }
      : {
          sx: {
            "&::-webkit-scrollbar": {
              height: "8px",
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#F1F1F1",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#BFBFBF",
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: "#A1A1A1",
              },
            },
          },
        },

    manualPagination: true,
    rowCount,
    positionActionsColumn: "last",

    icons,
    muiTableBodyRowProps: muiTableBodyRowProps || {
      sx: {
        "&:hover td": {
          backgroundColor:
            tableTheme === "CMMS"
              ? "#FCFCFC !important"
              : tableTheme === "DMS"
              ? "#edebeb"
              : rowHoverColor
              ? `${rowHoverColor} !important`
              : "#f5f5f5 !important",
        },
      },
    },
    muiColumnActionsButtonProps: {
      sx: {
        position: "absolute !important",
        left: "-6px !important" ,
        top: "80% !important",
        transform: "translateY(-50%) !important",
        zIndex: 2,
      },
    },
    // Update header cell styles
    muiTableHeadCellProps: {
      sx: (theme) => ({
        backgroundColor: headerBackgroundColor
          ? headerBackgroundColor
          : tableTheme === "CMMS"
          ? "#FAFAFA"
          : tableTheme == "TenantAdmin"
          ? "#FFFFFF"
          : tableTheme === "DMS"
          ? "#fff"
          : tableTheme == "RMS"
          ? "#226eb7 !important"
          : null,
        borderTop: "1px solid #E5E7EB",
        color:
          tableTheme === "CMMS"
            ? "#616161"
            : tableTheme === "DMS"
            ? "#212529"
            : tableTheme == "TenantAdmin"
            ? "#342E37"
            : tableTheme == "RMS"
            ? "#FFFFFF"
            : headerFontColor || "#374151",
        fontSize: tableTheme == "TenantAdmin" ? "12px" : "0.875rem",
        fontWeight:
          tableTheme === "CMMS"
            ? 300
            : tableTheme === "DMS"
            ? 600
            : tableTheme == "TenantAdmin"
            ? 400
            : 500,
        letterSpacing: "0.01em",
        textTransform: "none",
        padding:
          tableTheme == "TenantAdmin" ? "8px 15px 8px 10px" : "12px 16px",
        borderRight: enableVerticalBorders ? "1px solid #E5E7EB" : "none",
        "&:last-child": {
          borderRight: "none",
        },
        '& [aria-label^="Filtering by"]': {
          display: "none !important",
        },

        "& .Mui-TableHeadCell-Content-Labels": {
          justifyContent: "flex-start", // align text left
          paddingLeft: "17px",
          alignItems: "center",
          whiteSpace: "nowrap !important", // prevent wrapping
          overflow: "visible",
        },
        "& .Mui-TableHeadCell-Content-Wrapper": {
          whiteSpace: "nowrap", // also prevent wrapping inside wrapper
          overflow: "visible",
          display: "flex",
          alignItems: "center",
        },
        "& .MuiButtonBase-root.MuiTableSortLabel-root": {
          whiteSpace: "nowrap", // prevent sort label from wrapping
        },
        "& .Mui-active": {
          color: headerFontColor || theme.palette.primary.main,
        },
        "& .MuiSvgIcon-root": {
          color:
            tableTheme == "RMS"
              ? "#FFFFFF !important"
              : tableTheme == "TenantAdmin"
              ? "#342E37 !important"
              : `#6B7280 !important`,
        },
        "& .MuiIconButton-root": {
          color:
            tableTheme == "RMS"
              ? "#FFFFFF !important"
              : tableTheme == "TenantAdmin"
              ? "#342E37 !important"
              : `#6B7280 !important`,
        },
        "& .MuiButtonBase-root": {
          color:
            tableTheme == "RMS"
              ? "#FFFFFF !important"
              : tableTheme == "TenantAdmin"
              ? "#342E37 !important"
              : `#6B7280 !important`,
        },
        "& .MuiTableSortLabel-root": {
          display: "flex !important",
          alignItems: "center !important",
          justifyContent: "center !important",
        },
        "& .MuiTableSortLabel-icon": {
          display: "inline-block !important",
          fontSize: "1.1rem !important",
        },
        "& .MuiTableSortLabel-iconDirectionAsc": {
          transform: "rotate(180deg) !important",
        },
        "& .MuiTableSortLabel-iconDirectionDesc": {
          transform: "rotate(0deg) !important",
        },
        // "& .MuiButtonBase-root.MuiIconButton-root": {
        //   margin: "-8px -4px !important",
        // },

        "& .MuiFormControl-root": {
          paddingLeft: "3px !important",
        },
        "& .MuiInputAdornment-root": {
          marginRight: "5px !important", // change this to what you want
          padding:"0px"
        },

        "& .MuiInputAdornment-root .MuiIconButton-root": {
          marginRight: "3px !important", // directly on the button
          padding: "0px !important", // adjust padding if needed
        },
      }),
    },

    // Update body cell styles
    muiTableBodyCellProps: {
      sx: {
        fontSize: "0.885rem",
        color:
          tableTheme === "CMMS"
            ? "#1B1B1B"
            : tableTheme === "DMS"
            ? "#212529"
            : tableTheme == "RMS"
            ? "#342e37"
            : tableTheme == "TenantAdmin"
            ? "#342E37"
            : "#4B5563",
        fontWeight:
          tableTheme === "CMMS"
            ? 350
            : tableTheme === "DMS"
            ? 400
            : tableTheme == "RMS"
            ? 400
            : tableTheme == "TenantAdmin"
            ? 400
            : 500,
        padding:
          tableTheme == "DMS"
            ? "13px 16px"
            : tableTheme == "TenantAdmin"
            ? "15px"
            : "12px 16px",
        borderRight: enableVerticalBorders ? "1px solid #E5E7EB" : "none",
        "&:last-child": {
          borderRight: "none",
        },
      },
    },

    // Update pagination text
    muiPaginationProps: {
      ...muiPaginationProps,
      sx: {
        ".MuiTypography-root": {
          fontSize: "0.875rem",
          fontWeight: 500,
          color: "#374151",
        },
      },
    },

    // Update paper styles for better contrast
    muiTablePaperProps: {
      sx: {
        borderRadius:
          tableTheme === "CMMS"
            ? "0px"
            : tableTheme == "RMS"
            ? "0px"
            : tableTheme === "DMS"
            ? "0px"
            : tableTheme == "TenantAdmin"
            ? "0px"
            : "12px",
        boxShadow:
          tableTheme == "TenantAdmin"
            ? "none !important"
            : "0 1px 3px 0 rgb(0 0 0 / 0.1)",
        border:
          tableTheme === "DMS"
            ? "none"
            : tableTheme == "RMS"
            ? "1px solid #dcdcdc"
            : tableTheme == "TenantAdmin"
            ? "1px solid #dcdcdc"
            : "1px solid #E5E7EB",
      },
      elevation: 0,
    },
    // muiTablePaginationProps: {
    //     SelectProps: {
    //         sx: {
    //             display: 'flex !important',
    //             alignItems: 'center !important',
    //             height: '40px !important',
    //             padding: '0 8px !important',
    //             '& .MuiSelect-select': {
    //                 display: 'flex !important',
    //                 alignItems: 'center !important',
    //             },
    //         },
    //     },
    // }
  });

  const handleSaveView = () => {
    const tableState = {
      name: viewName,
      columnFilters: table.getState().columnFilters,
      globalFilters: table.getState().globalFilters,
      sorting: table.getState().sorting,
      pagination: table.getState().pagination,
      columnVisibility: table.getState().columnVisibility,
    };
    if (onSaveTableState) {
      onSaveTableState(tableState);
    } else {
      const savedViews = JSON.parse(localStorage.getItem("tableViews") || "[]");
      savedViews.push(tableState);
      localStorage.setItem("tableViews", JSON.stringify(savedViews));
    }
    setSaveViewOpen(false);
    setViewName("");
  };

  return (
    <>
      <MaterialReactTable table={table} />
      <Dialog
        open={saveViewOpen}
        onClose={() => setSaveViewOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            width: "400px",
            p: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "1.125rem",
            fontWeight: 500,
            color: "#111827",
            pb: 1,
          }}
        >
          Save View
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="View Name"
            fullWidth
            value={viewName}
            onChange={(e) => setViewName(e.target.value)}
            sx={{
              mt: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={() => setSaveViewOpen(false)}
            sx={{
              color: "#6B7280",
              "&:hover": {
                backgroundColor: "#F3F4F6",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleSaveView(table);
            }}
            variant="contained"
            disabled={!viewName}
            sx={{
              backgroundColor: "#2563EB",
              "&:hover": {
                backgroundColor: "#1E40AF",
              },
              "&.Mui-disabled": {
                backgroundColor: "#93C5FD",
                color: "#ffffff",
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MUIDataTable;
